import React from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth, can } from "@/auth";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { useCountry } from "@/country";
import AssistantWidget from "@/components/AssistantWidget";
import NotificationsBell from "@/components/NotificationsBell";
import {
  ChartLine,
  Database,
  Target,
  ClipboardText,
  CalendarBlank,
  CalendarCheck,
  Users,
  CurrencyEur,
  ChartBar,
  MapTrifold,
  Trophy,
  Gear,
  ShieldCheck,
  SignOut,
  Translate,
  Globe,
  CaretDown,
  List,
  X,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", labelKey: "nav.dashboard", icon: ChartLine, end: true },
  { to: "/installed-base", labelKey: "nav.installedBase", icon: Database },
  { to: "/opportunities", labelKey: "nav.opportunities", icon: Target },
  { to: "/account-plans", labelKey: "nav.accountPlans", icon: ClipboardText },
  { to: "/pipeline", labelKey: "nav.pipeline", icon: CalendarBlank },
  { to: "/objectives", labelKey: "nav.objectives", icon: Trophy },
  { to: "/visits", labelKey: "nav.visits", icon: CalendarCheck },
  { to: "/contacts", labelKey: "nav.contacts", icon: Users },
  { to: "/prices", labelKey: "nav.prices", icon: CurrencyEur, role: "admin" },
  { to: "/competitors", labelKey: "nav.competitors", icon: ChartBar },
  { to: "/map", labelKey: "nav.map", icon: MapTrifold },
  { to: "/settings", labelKey: "nav.settings", icon: Gear, role: "admin" },
  { to: "/admin", labelKey: "nav.admin", icon: ShieldCheck, role: "admin" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useI18n();
  const { currency, setCurrency } = useCurrency();
  const { code: country, setCode: setCountry, list: countries, current, loadError, reload: reloadCountries } = useCountry();
  const navigate = useNavigate();
  const [countryOpen, setCountryOpen] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const onCountryPick = (code) => {
    setCountry(code);
    setCountryOpen(false);
    setSidebarOpen(false);
    window.location.reload();
  };

  return (
    <div className="min-h-screen md:flex" style={{ background: "var(--bg)" }}>
      {/* Mobile topbar */}
      <header
        className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3"
        style={{ background: "rgba(251,251,253,0.92)", backdropFilter: "blur(20px)", borderBottom: "1px solid var(--border)" }}
      >
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-[8px] hover:bg-[var(--bg-subtle)] transition-colors"
            data-testid="sidebar-open-btn"
            aria-label="Open menu"
          >
            <List size={20} weight="bold" />
          </button>
          <div className="brand-mark">
            <span className="text-[15px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>{t("app.name")}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <NotificationsBell />
          <button
            onClick={() => { setSidebarOpen(true); setTimeout(() => setCountryOpen(true), 100); }}
            className="px-2 py-1.5 rounded-[8px] hover:bg-[var(--bg-subtle)] transition-colors flex items-center gap-1.5"
            data-testid="mobile-country-btn"
            aria-label="Switch country"
          >
            <span style={{ fontSize: 16 }}>{current.flag || "🌐"}</span>
            <span className="text-[12px] font-mono font-semibold">{country}</span>
          </button>
        </div>
      </header>

      {/* Backdrop for mobile drawer */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-[1000]"
          style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar — drawer on mobile, static on md+ */}
      <aside
        className={`w-[248px] shrink-0 flex flex-col fixed md:sticky inset-y-0 left-0 z-[1010] md:z-30 h-screen md:top-0 transition-transform duration-300 ease-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        style={{ background: "var(--bg-elevated)", borderRight: "1px solid var(--border)" }}
        data-testid="sidebar"
      >
        <div className="px-6 py-7 flex items-start justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
          <div>
            <div className="brand-mark">
              <span className="text-[18px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
                {t("app.name")}
              </span>
            </div>
            <div className="mt-2 text-[11px] tracking-wide" style={{ color: "var(--text-tertiary)" }}>
              {t("app.subtitle")}
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-2 -mr-2 -mt-1 rounded-[8px] hover:bg-[var(--bg-subtle)] transition-colors"
            data-testid="sidebar-close-btn"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Country switcher */}
        <div className="px-3 pt-3 pb-1 relative" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="label-tech mb-1.5 px-2" style={{ fontSize: 10 }}>{t("country.switcher")}</div>
          <button
            onClick={() => setCountryOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-[10px] hover:bg-[var(--bg-subtle)] transition-colors"
            data-testid="country-switcher"
            style={{ border: "1px solid var(--border)" }}
          >
            <span className="flex items-center gap-2 text-[13px] font-medium" style={{ color: "var(--text-primary)" }}>
              <span style={{ fontSize: 16 }}>{current.flag || "🌐"}</span>
              <span>{current.name_en || country}</span>
            </span>
            <CaretDown size={12} weight="bold" style={{ color: "var(--text-tertiary)" }} />
          </button>
          {countryOpen && (
            <div className="absolute left-3 right-3 mt-1 z-20 rounded-[12px] py-1 fade-in"
                 style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", boxShadow: "var(--shadow-lg)" }}
                 data-testid="country-dropdown">
              {countries.length === 0 ? (
                <div className="px-3 py-3 text-[12px] text-center" style={{ color: "var(--text-tertiary)" }}>
                  {loadError ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); reloadCountries(); }}
                      className="font-mono hover:underline"
                      data-testid="country-retry"
                      style={{ color: "var(--accent)" }}
                    >
                      ⚠ {lang === "fr" ? "Erreur — réessayer" : "Failed — retry"}
                    </button>
                  ) : (
                    <span className="font-mono">{lang === "fr" ? "Chargement…" : "Loading…"}</span>
                  )}
                </div>
              ) : countries.map((c) => (
                <button
                  key={c.code}
                  onClick={() => onCountryPick(c.code)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-[13px] hover:bg-[var(--bg-subtle)] transition-colors text-left"
                  data-testid={`country-option-${c.code}`}
                >
                  <span className="flex items-center gap-2">
                    <span style={{ fontSize: 16 }}>{c.flag}</span>
                    <span style={{ color: c.has_data ? "var(--text-primary)" : "var(--text-tertiary)" }}>
                      {c.name_en}
                    </span>
                  </span>
                  {!c.has_data && (
                    <span className="text-[10px] font-mono uppercase" style={{ color: "var(--text-tertiary)" }}>
                      {t("country.noData")}
                    </span>
                  )}
                  {c.code === country && (
                    <span className="text-[10px]" style={{ color: "var(--accent)" }}>●</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <nav className="flex-1 py-4 overflow-y-auto px-3">
          {NAV.map(({ to, labelKey, icon: Icon, end, role }) => {
            if (role && !can(user, role)) return null;
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${labelKey.replace(/\./g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 mb-0.5 text-[13.5px] font-medium rounded-[10px] transition-all duration-200 ${
                    isActive
                      ? "bg-[var(--bg-subtle)] text-[var(--text-primary)]"
                      : "text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={17} weight={isActive ? "fill" : "regular"} style={{ color: isActive ? "var(--accent)" : "currentColor" }} />
                    <span>{t(labelKey)}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4 space-y-3" style={{ borderTop: "1px solid var(--border)" }}>
          {/* Currency switcher */}
          <div className="px-2">
            <div className="label-tech mb-1.5" style={{ fontSize: 10 }}>Currency</div>
            <div className="flex gap-1" data-testid="currency-switcher">
              {["EUR", "USD", "TRY"].map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  data-testid={`currency-${c}`}
                  className={`flex-1 py-1 text-[11px] font-mono rounded-full transition-colors ${
                    currency === c
                      ? "bg-[var(--text-primary)] text-white"
                      : "text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
                  }`}
                  style={{ border: currency === c ? "none" : "1px solid var(--border)" }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-2 px-2 text-[11px] hover:text-[var(--text-primary)] transition-colors font-mono"
            style={{ color: "var(--text-secondary)" }}
          >
            <Translate size={13} />
            <span>{lang.toUpperCase()} → {lang === "en" ? "FR" : "EN"}</span>
          </button>

          {user && (
            <div className="space-y-1 px-2">
              <div className="text-[11px] font-mono truncate" style={{ color: "var(--text-tertiary)" }}>{user.email}</div>
              <div className="flex items-center justify-between gap-2">
                <span className="pill" data-testid="user-role">{user.role}</span>
                <div className="hidden md:block">
                  <NotificationsBell inSidebar />
                </div>
                <button
                  onClick={onLogout}
                  data-testid="logout-btn"
                  className="flex items-center gap-1 text-[11px] hover:text-[var(--accent)] transition-colors"
                  style={{ color: "var(--text-secondary)" }}
                >
                  <SignOut size={13} />
                  {t("nav.logout")}
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 pb-24 md:pb-0" style={{ background: "var(--bg)" }}>
        <Outlet />
      </main>

      {/* AI Assistant — floating bubble visible on every authenticated page */}
      <AssistantWidget />
    </div>
  );
}
